import {
  HighlightIcon,
  type HighlightIconVariant,
  type IconName,
} from '@dev-spendesk/grapes';
import cx from 'classnames';

import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';

import './PbrRuleTimeline.css';

type PbrRuleTimelineProps = {
  incompletePaymentsLimit: number;
  completionDeadline: number;
  mode: 'requester' | 'admin';
  className?: string;
};

export const PbrRuleTimeline = ({
  incompletePaymentsLimit,
  completionDeadline,
  mode,
  className,
}: PbrRuleTimelineProps) => {
  const { t } = useTranslation('global');

  const translations = getTranslations(
    {
      incompletePaymentsLimit,
      completionDeadline,
      mode,
    },
    t,
  );

  const steps: {
    key: string;
    title?: string | React.ReactElement;
    description?: string | React.ReactElement;
    icon: string;
    variant: HighlightIconVariant;
  }[] = [
    {
      key: 'step-1',
      ...translations.steps[0],
      icon: mode === 'requester' ? 'card' : 'triangle-warning',
      variant: mode === 'requester' ? 'purple' : 'apricot',
    },
    {
      key: 'step-2',
      ...translations.steps[1],
      icon: mode === 'requester' ? 'clock-filled' : 'triangle-warning',
      variant: 'apricot',
    },
    {
      key: 'step-3',
      ...translations.steps[2],
      icon: 'triangle-warning',
      variant: 'apricot',
    },
    {
      key: 'step-4',
      ...translations.steps[3],
      icon: 'lock-close',
      variant: 'peach',
    },
    {
      key: 'step-5',
      ...translations.steps[4],
      icon: 'lock-open',
      variant: 'forest',
    },
  ];

  return (
    <div className={cx('PbrRuleTimeline', className)}>
      {translations.mainTitle && (
        <div className="text-content-primary title-m mb-24">
          {translations.mainTitle}
        </div>
      )}
      <div className="PbrRuleTimeline__desc">
        {steps.map((step) => (
          <div key={step.key} className="PbrRuleTimeline__desc__row">
            <div className="PbrRuleTimeline__desc__row__icon">
              <HighlightIcon
                name={step.icon as IconName}
                variant={step.variant}
                size={32}
              />
              <div className="PbrRuleTimeline__desc__row__icon__line" />
            </div>
            <div className="body-m flex flex-col content-stretch gap-4 font-medium">
              {step.title && (
                <p className="text-content-primary leading-[20px]">
                  {step.title}
                </p>
              )}
              {step.description && (
                <p className="text-content-secondary-bg-primary leading-[20px]">
                  {step.description}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const getTranslations = (
  { incompletePaymentsLimit, completionDeadline, mode }: PbrRuleTimelineProps,
  t: TGlobalFunctionTyped,
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  if (mode === 'requester') {
    return {
      mainTitle: t('controlRules.pbrTimeline.requester.title'),
      steps: [
        {
          title: t(
            'controlRules.pbrTimeline.requester.mandatoryFields.gracePeriod.title',
          ),
          description: t(
            'controlRules.pbrTimeline.requester.mandatoryFields.gracePeriod.description',
            { count: completionDeadline },
          ),
        },
        {
          title: t(
            'controlRules.pbrTimeline.requester.mandatoryFields.urgent.title',
            { count: completionDeadline - 1 },
          ),
          description: t(
            'controlRules.pbrTimeline.requester.mandatoryFields.urgent.description',
          ),
        },
        {
          title: t(
            'controlRules.pbrTimeline.requester.mandatoryFields.late.title',
            { count: completionDeadline },
          ),
          description: t(
            'controlRules.pbrTimeline.requester.mandatoryFields.late.description',
          ),
        },
        {
          title: t(
            'controlRules.pbrTimeline.requester.mandatoryFields.blocked.title',
            { count: incompletePaymentsLimit },
          ),
          description: t(
            'controlRules.pbrTimeline.requester.mandatoryFields.blocked.description',
          ),
        },
        {
          title: t(
            'controlRules.pbrTimeline.requester.mandatoryFields.unblocking.title',
          ),
          description: t(
            'controlRules.pbrTimeline.requester.mandatoryFields.unblocking.description',
          ),
        },
      ],
    };
  }
  return {
    mainTitle: t('controlRules.pbrTimeline.admin.title'),
    steps: [
      {
        title: t(
          'controlRules.pbrTimeline.admin.mandatoryFields.gracePeriod.title',
          {
            interval: `1-${completionDeadline}`,
          },
        ),
        description: t(
          'controlRules.pbrTimeline.admin.mandatoryFields.gracePeriod.description',
        ),
      },
      {
        title: t(
          'controlRules.pbrTimeline.admin.mandatoryFields.urgent.title',
          {
            count: completionDeadline - 1,
          },
        ),
        description: t(
          'controlRules.pbrTimeline.admin.mandatoryFields.urgent.description',
        ),
      },
      {
        title: t('controlRules.pbrTimeline.admin.mandatoryFields.late.title', {
          count: completionDeadline,
        }),
        description: t(
          'controlRules.pbrTimeline.admin.mandatoryFields.late.description',
        ),
      },
      {
        title: t(
          'controlRules.pbrTimeline.admin.mandatoryFields.blocked.title',
          {
            count: incompletePaymentsLimit ?? 1,
          },
        ),
        description: t(
          'controlRules.pbrTimeline.admin.mandatoryFields.blocked.description',
        ),
      },
      {
        title: t(
          'controlRules.pbrTimeline.admin.mandatoryFields.unblocking.title',
        ),
        description: t(
          'controlRules.pbrTimeline.admin.mandatoryFields.unblocking.description',
        ),
      },
    ],
  };
};
